import React from 'react';
import { Link } from 'react-router-dom';
import { Printer, ArrowLeft } from 'lucide-react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { useRef } from 'react';

const QRS = [
  { fr: 'PLATS', en: 'Meals', price: '13.90', img: '/qr/twint-plats.png' },
  { fr: 'ENTRÉES & APÉRO', en: 'Starters & apéro', price: '7.50', img: '/qr/twint-entrees.png' },
  { fr: 'DESSERTS', en: 'Desserts', price: '3.50', img: '/qr/twint-desserts.png' },
  { fr: 'BOISSONS', en: 'Drinks', price: '3.00', img: '/qr/twint-boissons.png' },
];

const ink = '#2f3826';
const olive = '#4b5e34';

function Piece({ children, w, h, label }) {
  return (
    <div className="mb-10">
      <p className="no-print text-xs uppercase tracking-widest text-neutral-400 mb-2">{label}</p>
      <div className="piece bg-white shadow-lg mx-auto border border-dashed border-neutral-300" style={{ width: w, minHeight: h }}>
        {children}
      </div>
    </div>
  );
}

export default function AffichesPage() {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const shareQrRef = useRef(null);

  const downloadQr = () => {
    const canvas = shareQrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = 'qr-metio-convivialite.png';
    a.click();
  };

  return (
    <div className="min-h-screen bg-[#eceadf] py-8 px-4 print:bg-white print:p-0">
      <style>{`
        @page { size: A4 portrait; margin: 8mm; }
        @media print {
          .no-print { display: none !important; }
          .piece { page-break-after: always; box-shadow: none !important; border: none !important; margin: 0 auto !important; }
        }
      `}</style>

      <div className="no-print max-w-[210mm] mx-auto flex items-center justify-between mb-8">
        <Link to="/admin" className="flex items-center gap-2 text-sm text-neutral-600 hover:text-[#4b5e34]">
          <ArrowLeft size={16} /> Retour à l'admin
        </Link>
        <button data-testid="print-posters-btn" onClick={() => window.print()} className="flex items-center gap-2 bg-[#4b5e34] hover:bg-[#3f5230] text-white text-sm px-5 py-2.5 rounded-lg">
          <Printer size={16} /> Imprimer les 3 affiches
        </button>
      </div>

      {/* ---------- QR D'ACCÈS AU SITE (à partager) ---------- */}
      <Piece w="148mm" h="148mm" label="QR d'accès au site — à diffuser au groupe et aux utilisateurs">
        <div className="px-[10mm] py-[9mm] text-center" style={{ color: ink }} data-testid="share-qr-card">
          <h2 className="font-display font-bold" style={{ fontSize: '20pt' }}>Metio Frigo</h2>
          <p className="mb-4" style={{ fontSize: '11pt', color: olive }}>Commandez vos repas de la semaine · Order your weekly meals</p>
          <div ref={shareQrRef} className="inline-block p-3 border rounded-xl" style={{ borderColor: '#d5d2c4' }}>
            <QRCodeCanvas value={siteUrl} size={480} style={{ width: '62mm', height: '62mm' }} />
          </div>
          <p className="mt-3 text-neutral-500" style={{ fontSize: '9pt' }}>{siteUrl}</p>
          <button onClick={downloadQr} data-testid="download-share-qr" className="no-print mt-3 inline-flex items-center gap-2 bg-[#4b5e34] hover:bg-[#3f5230] text-white text-sm px-5 py-2.5 rounded-lg">
            Télécharger le QR en PNG
          </button>
          <p className="no-print mt-2 text-neutral-400" style={{ fontSize: '8pt' }}>Astuce : ouvrez cette page depuis le site publié pour que le QR encode l'adresse définitive.</p>
        </div>
      </Piece>

      {/* ---------- PIECE 1 — DOOR CARD (A4) ---------- */}
      <Piece w="210mm" h="297mm" label="Pièce 1 — Carte de porte (A4, à plastifier)">
        <div className="px-[12mm] py-[10mm] flex flex-col h-full" style={{ color: ink }} data-testid="poster-door-card">
          <div className="text-center border-b-2 pb-4 mb-5" style={{ borderColor: olive }}>
            <h1 className="font-display font-bold" style={{ fontSize: '30pt', lineHeight: 1.1 }}>FRIDGE @METIO</h1>
            <p className="font-display" style={{ fontSize: '15pt', color: olive }}>Des repas frais, à toute heure</p>
            <p className="text-neutral-500" style={{ fontSize: '10pt' }}>Fresh meals, any time</p>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-5 text-center">
            {[
              { fr: 'Choisissez votre article dans le frigo.', en: 'Pick your item from the fridge.' },
              { fr: 'Scannez le code TWINT de sa catégorie (ci-dessous).', en: 'Scan the TWINT code for its category (below).' },
              { fr: 'Réchauffez au micro-ondes — bon appétit !', en: 'Heat it up in the microwave — enjoy!' },
            ].map((s, i) => (
              <div key={i}>
                <div className="mx-auto mb-1 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold" style={{ background: olive }}>{i + 1}</div>
                <p style={{ fontSize: '10.5pt' }} className="font-semibold leading-snug">{s.fr}</p>
                <p style={{ fontSize: '8pt' }} className="text-neutral-500 leading-snug">{s.en}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            {QRS.map((q) => (
              <div key={q.fr} className="border rounded-lg p-3 flex items-center gap-4" style={{ borderColor: '#d5d2c4' }}>
                <img src={q.img} alt={`TWINT ${q.fr}`} style={{ width: '42mm', height: '42mm' }} className="object-contain shrink-0" />
                <div>
                  <p className="font-bold tracking-wide" style={{ fontSize: '13pt' }}>{q.fr}</p>
                  <p className="text-neutral-500" style={{ fontSize: '8.5pt' }}>{q.en}</p>
                  <p className="font-display font-bold" style={{ fontSize: '21pt', color: olive }}>CHF {q.price}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center font-semibold mb-1" style={{ fontSize: '11pt' }}>Plusieurs articles ? Scannez un code par article — merci !</p>
          <p className="text-center text-neutral-500 mb-4" style={{ fontSize: '8.5pt' }}>Several items? One scan per item — thank you!</p>

          <p className="text-center italic mb-1" style={{ fontSize: '9.5pt' }}>
            Frigo en libre-service, basé sur la confiance — merci de scanner avant de déguster. Température et stock contrôlés régulièrement.
          </p>
          <p className="text-center italic text-neutral-500 mb-4" style={{ fontSize: '8pt' }}>
            Self-service fridge, built on trust — please scan before you tuck in. Temperature and stock are checked regularly.
          </p>

          <div className="flex items-center justify-center gap-6 border-t border-b py-3 mb-3" style={{ borderColor: '#d5d2c4' }}>
            <div className="flex items-center gap-2">
              <div data-testid="website-qr" style={{ width: '22mm', height: '22mm' }} className="shrink-0">
                <QRCodeSVG value={typeof window !== 'undefined' ? window.location.origin : ''} style={{ width: '100%', height: '100%' }} />
              </div>
              <p style={{ fontSize: '9pt' }} className="leading-snug">Menu de la semaine<br /><span className="text-neutral-500" style={{ fontSize: '7.5pt' }}>This week's menu</span></p>
            </div>
            <div className="flex items-center gap-2">
              <img src="/qr/whatsapp.png" alt="QR WhatsApp" style={{ width: '22mm', height: '22mm' }} />
              <p style={{ fontSize: '9pt' }} className="leading-snug">Un souci ? WhatsApp<br /><b>077 242 61 65</b><br /><span className="text-neutral-500" style={{ fontSize: '7.5pt' }}>A problem? WhatsApp</span></p>
            </div>
          </div>

          <p className="text-neutral-500 mt-auto" style={{ fontSize: '6.5pt', lineHeight: 1.4 }}>
            Exploité par SCC Biopôle Sàrl, c/o Hexopus SA, Route de Genève 15, 1033 Cheseaux-sur-Lausanne · Prix en CHF, TVA 2.6% incluse · Allergènes et valeurs nutritionnelles : voir l'emballage de chaque produit.<br />
            Operated by SCC Biopôle Sàrl, c/o Hexopus SA, Route de Genève 15, 1033 Cheseaux-sur-Lausanne · Prices in CHF incl. 2.6% VAT · Allergens and nutrition: see each product's packaging.
          </p>
        </div>
      </Piece>

      {/* ---------- PIECE 2 — MICROWAVE CARD (A5) ---------- */}
      <Piece w="148mm" h="210mm" label="Pièce 2 — Carte micro-ondes (A5, à plastifier)">
        <div className="px-[10mm] py-[9mm]" style={{ color: ink }} data-testid="poster-microwave-card">
          <h2 className="font-display font-bold mb-2" style={{ fontSize: '18pt', color: olive }}>Réchauffage express 🔥</h2>
          <ol className="list-decimal ml-5 mb-1 space-y-1" style={{ fontSize: '11pt' }}>
            <li>Retirez le fourreau en carton et percez le film (ou soulevez un coin).</li>
            <li>3 minutes à 750 W.</li>
            <li>Mélangez, laissez reposer 30 secondes — c'est prêt.</li>
          </ol>
          <p className="text-neutral-500 mb-3" style={{ fontSize: '8.5pt' }}>Les instructions exactes figurent sur chaque emballage.</p>

          <h3 className="font-display font-bold mb-1" style={{ fontSize: '14pt', color: olive }}>Après le repas ♻️</h3>
          <p style={{ fontSize: '10.5pt' }}>Barquettes et films → incinérable · Canettes et PET → bac de tri</p>
          <p className="mb-4" style={{ fontSize: '10.5pt' }}>Merci de laisser le micro-ondes et l'évier propres pour la personne suivante 🙂</p>

          <div className="border-t pt-3" style={{ borderColor: '#d5d2c4' }}>
            <p className="font-semibold text-neutral-600 mb-1" style={{ fontSize: '9.5pt' }}>Quick reheat 🔥</p>
            <p className="text-neutral-500" style={{ fontSize: '8.5pt', lineHeight: 1.5 }}>
              1. Remove the cardboard sleeve and pierce the film (or lift a corner). 2. 3 minutes at 750 W. 3. Stir, rest 30 seconds — ready. Exact instructions are on each package.
            </p>
            <p className="font-semibold text-neutral-600 mt-2 mb-1" style={{ fontSize: '9.5pt' }}>After your meal ♻️</p>
            <p className="text-neutral-500" style={{ fontSize: '8.5pt', lineHeight: 1.5 }}>
              Trays and film → general waste · Cans and PET → recycling bin. Please leave the microwave and sink clean for the next person 🙂
            </p>
          </div>
        </div>
      </Piece>

      {/* ---------- PIECE 3 — PROBLEM CARD (A6) ---------- */}
      <Piece w="105mm" h="148mm" label="Pièce 3 — Carte problème (A6, sur la porte du frigo)">
        <div className="px-[8mm] py-[7mm]" style={{ color: ink }} data-testid="poster-problem-card">
          <h2 className="font-display font-bold mb-1" style={{ fontSize: '17pt', color: olive }}>Un souci ?</h2>
          <p className="mb-2" style={{ fontSize: '9.5pt' }}>Paiement raté, article manquant, frigo vide ou trop chaud ?</p>
          <div className="flex items-center gap-3 mb-2">
            <img src="/qr/whatsapp.png" alt="QR WhatsApp" style={{ width: '26mm', height: '26mm' }} />
            <p style={{ fontSize: '11pt' }}>💬 WhatsApp :<br /><b style={{ fontSize: '13pt' }}>077 242 61 65</b></p>
          </div>
          <p style={{ fontSize: '9pt' }}>Réponse rapide — remboursement TWINT le jour même si besoin.</p>
          <p className="mb-3" style={{ fontSize: '9pt' }}>Une photo du rayon vide nous aide beaucoup, merci !</p>
          <div className="border-t pt-2 text-neutral-500" style={{ borderColor: '#d5d2c4', fontSize: '7.5pt', lineHeight: 1.5 }}>
            <b>Something wrong?</b> Payment failed, item missing, fridge empty or too warm? 💬 WhatsApp: <b>077 242 61 65</b>. Quick reply — same-day TWINT refund if needed. A photo of the empty shelf helps us a lot, thank you!
          </div>
        </div>
      </Piece>
    </div>
  );
}
